import type { HttpError } from '@refinedev/core'
import { CanAccess, useList } from '@refinedev/core'
import type { AuditEvent } from '@sirius/contracts/admin'
import { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Table as UiTable,
} from '~/components/ui/table'
import { formatDateTime } from '~/i18n/format'
import type { GameNames } from '~/pages/cabinets/gameNames'
import { useActiveTenant, useBaseDomain } from '~/tenancy/activeTenant'
import { actionLabel } from './activityFormat'
import { ActorName, ChangesTable, ChangesToggle } from './cells'
import type { HistoryRecord, ViewAllLink } from './recordHistory'
import {
  HISTORY_PAGE_SIZE,
  HISTORY_SORTERS,
  historyFilters,
  historyResources,
  viewAllLink,
} from './recordHistory'

type HistoryProps = {
  record: HistoryRecord
  /** the slug of the record's tenant, for "View all" on another tenant's host */
  tenantSlug?: string
  /** a cabinet's games show by name in the changes */
  gameNames?: GameNames
}

/**
 * A record's own history on its show page: its latest events from the
 * activity log (`audit.list`), newest first, with "View all" to `/activity`
 * narrowed to it. Only for those who may read the activity log (staff and
 * the host tenant's owners/admins): nothing for members. Staff actors are
 * redacted for tenant users by the API, like in the log. A platform record
 * (a game, no tenant) shows its staff-only events from the platform log, for
 * staff only, with "View all" to `/platform/activity`.
 */
export function History(props: HistoryProps) {
  return (
    <CanAccess
      resource={historyResources(props.record).access}
      action="list"
      fallback={null}
    >
      <HistorySection {...props} />
    </CanAccess>
  )
}

function ViewAll({ link }: { link: ViewAllLink }) {
  const { t } = useTranslation()
  const className = 'text-sm underline underline-offset-4 hover:text-primary'
  return link.kind === 'route' ? (
    <Link
      className={className}
      to={link.to}
    >
      {t('activityLog.history.viewAll')}
    </Link>
  ) : (
    <a
      className={className}
      href={link.href}
    >
      {t('activityLog.history.viewAll')}
    </a>
  )
}

function HistorySection({ record, tenantSlug, gameNames }: HistoryProps) {
  const { t } = useTranslation()
  const activeTenant = useActiveTenant()
  const baseDomain = useBaseDomain()
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set())
  const { result, query } = useList<AuditEvent, HttpError>({
    resource: historyResources(record).list,
    pagination: { currentPage: 1, pageSize: HISTORY_PAGE_SIZE },
    sorters: HISTORY_SORTERS,
    filters: historyFilters(record),
  })
  const events = result.data
  const total = query.isSuccess ? result.total : undefined
  const link = viewAllLink(record, {
    hostTenantId: activeTenant.id,
    tenantSlug,
    baseDomain,
    location: window.location,
  })

  const toggle = (id: string) => {
    setExpanded((current) => {
      const next = new Set(current)
      if (!next.delete(id)) {
        next.add(id)
      }
      return next
    })
  }

  return (
    <section
      aria-labelledby="history-heading"
      className="max-w-3xl"
    >
      <div className="mb-2 flex items-baseline gap-4">
        <h3
          id="history-heading"
          className="font-semibold"
        >
          {t('activityLog.history.heading')}
        </h3>
        {link !== null && total !== undefined && total > 0 && (
          <ViewAll link={link} />
        )}
      </div>
      {query.isError && (
        <p
          role="alert"
          className="text-destructive text-sm"
        >
          {t('activityLog.history.loadError', {
            message: query.error.message,
          })}
        </p>
      )}
      {query.isPending && !query.isError && (
        <p className="text-muted-foreground text-sm">
          {t('activityLog.history.loading')}
        </p>
      )}
      {total === 0 && (
        <p className="text-muted-foreground text-sm">
          {t('activityLog.history.empty')}
        </p>
      )}
      {events.length > 0 && (
        <UiTable
          aria-label={t('activityLog.history.tableLabel')}
          className="text-sm"
        >
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <span className="sr-only">
                  {t('activityLog.column.changes')}
                </span>
              </TableHead>
              <TableHead className="w-48">
                {t('activityLog.column.time')}
              </TableHead>
              <TableHead>{t('activityLog.column.actor')}</TableHead>
              <TableHead className="w-36">
                {t('activityLog.column.action')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {events.map((event) => {
              const isExpanded = expanded.has(event.id)
              return (
                <Fragment key={event.id}>
                  <TableRow>
                    <TableCell>
                      <ChangesToggle
                        expanded={isExpanded}
                        onToggle={() => toggle(event.id)}
                      />
                    </TableCell>
                    <TableCell>{formatDateTime(event.createdAt)}</TableCell>
                    <TableCell className="max-w-64">
                      <ActorName actor={event.actor} />
                    </TableCell>
                    <TableCell>{actionLabel(event.action, t)}</TableCell>
                  </TableRow>
                  {isExpanded && (
                    <TableRow className="hover:bg-transparent">
                      <TableCell
                        colSpan={4}
                        className="bg-muted/30 whitespace-normal"
                      >
                        <ChangesTable
                          event={event}
                          gameNames={gameNames}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              )
            })}
          </TableBody>
        </UiTable>
      )}
      {total !== undefined && total > events.length && (
        <p className="text-muted-foreground mt-2 text-sm">
          {t('activityLog.history.latest', {
            shown: events.length,
            total,
          })}
        </p>
      )}
    </section>
  )
}
